import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { IoClose, IoFolderOutline } from 'react-icons/io5';
import { MdCreateNewFolder, MdCheck } from 'react-icons/md';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Zoom from '@mui/material/Zoom';
import ProjectFolderService from '@Helpers/URLHelpers/ProjectFolderService';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import ErrorHandler from '@Root/ErrorHandler';
import './CreateFolderForm.scss';

export default function CreateFolderForm(props) {

    const [cursor, setCursor] = useState('auto');
    const [folderName, setFolderName] = useState('');
    const { currentProjectId } = useReduxProject();
    const projectFolderService = new ProjectFolderService();
    const errorHandler = new ErrorHandler();
    const className = 'createFolderForm';

    useEffect(() => {

    }, []);

    const handleSubmit = async(e) => {
        e.preventDefault();
        setCursor('wait');
        try {
            let parentType = props.navigationParentId === 0 ? 'project' : 'folder';
            let parentId = props.navigationParentId === 0 ? currentProjectId : props.navigationParentId;
            let projectFolder = {
                projectId: currentProjectId,
                folderName: folderName,
                parentId: parentId,
                parentType: parentType,
            }
            await projectFolderService.createProjectFolder(projectFolder);
            props.onClose();
            await props.renderData(parentId);
        } catch(err) {
            errorHandler.handleError(err);
        }
        setFolderName('');
        setCursor('auto');
    }

    const handleFolderNameChange = (e) => {
        let sanitizedFolderName = DOMPurify.sanitize(e.target.value);
        setFolderName(sanitizedFolderName);
    }

    return (
        <Modal
            open={props.open}
            onClose={props.onClose}
            className={className}>
            <Box className={className + '__container'} style={{cursor: cursor}}>
                {/* Header */}
                <Box className={className + '__header'}>
                    <Box className={className + '__header__left'}>
                        <Box className={className + '__header__icon'}>
                            <MdCreateNewFolder />
                        </Box>
                        <Box>
                            <Typography variant="h6" className={className + '__header__title'}>
                                Create New Folder
                            </Typography>
                            <Typography variant="body2" className={className + '__header__subtitle'}>
                                Enter a name for your new folder
                            </Typography>
                        </Box>
                    </Box>
                    <Tooltip title="Close" arrow placement="left" slots={{ transition: Zoom }}>
                        <IconButton 
                            onClick={props.onClose}
                            className={className + '__header__close'}
                            disabled={cursor === 'wait'}>
                            <IoClose />
                        </IconButton>
                    </Tooltip>
                </Box>

                <Divider />

                {/* Body */}
                <Box className={className + '__body'}>
                    <form onSubmit={handleSubmit} className={className + '__form'}>
                        <Box className={className + '__form__field'}>
                            <Typography variant="subtitle2" className={className + '__form__label'}>
                                Folder Name
                            </Typography>
                            <TextField
                                fullWidth
                                value={folderName}
                                onChange={handleFolderNameChange}
                                placeholder="e.g., Project Documents"
                                autoFocus
                                disabled={cursor === 'wait'}
                                className={className + '__form__input'}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <IoFolderOutline />
                                        </InputAdornment>
                                    ),
                                }}
                                helperText={`${folderName.length}/100 characters`}
                                inputProps={{ maxLength: 100 }}
                            />
                        </Box>

                        {/* Footer Actions */}
                        <Box className={className + '__form__actions'}>
                            <Button
                                variant="outlined"
                                onClick={props.onClose}
                                disabled={cursor === 'wait'}
                                className={className + '__form__actions__cancel'}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={folderName === '' || folderName.length === 0 || cursor === 'wait'}
                                className={className + '__form__actions__submit'}
                                startIcon={cursor === 'wait' ? <CircularProgress size={16} /> : <MdCheck />}>
                                {cursor === 'wait' ? 'Creating...' : 'Create Folder'}
                            </Button>
                        </Box>
                    </form>
                </Box>
            </Box>
        </Modal>
    )
}